import { CalendarRange } from 'lucide-react'
import { useId, useState } from 'react'
import { PopoverSheet } from '@/components/ui/PopoverSheet'
import { Select } from '@/components/ui/Select'
import type { MonthKey } from '@/lib/planning-types'
import { monthLabel, monthRange } from './month'

/**
 * Een reeks maanden in een keer vullen, naast het raster en niet in plaats
 * daarvan. Cel voor cel typen blijft de gewone weg; dit is er voor de zin
 * waarin werk in het echt wordt afgesproken: van september tot en met december,
 * veertig uur per maand.
 *
 * Het bereik wordt als twee maanden gevraagd en als losse maandregels
 * weggeschreven. Een kans heeft bewust geen begin- en einddatum - de maandregels
 * zijn de waarheid - dus dit is invoerhulp en geen tweede administratie. De
 * keuzelijst loopt daarom ook voorbij het venster van het scherm: waar je het
 * werk neerlegt hoort niet af te hangen van hoe ver je toevallig kijkt.
 */
export function MonthRangeFill({
  who,
  months,
  start,
  onFill,
}: {
  /** Van wie de rij is; staat in de knop en boven het paneel. */
  who: string
  /** De maanden waaruit je kunt kiezen, op volgorde en aaneengesloten. */
  months: MonthKey[]
  /** Waar de reeks standaard begint. */
  start: MonthKey
  onFill: (months: MonthKey[], hours: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState<MonthKey>(start)
  const [to, setTo] = useState<MonthKey>(start)
  const [hours, setHours] = useState('')
  const ids = useId()

  const options = months.map((month) => ({ value: month, label: monthLabel(month) }))
  const range = monthRange(from, to)

  const value = hours.trim().replace(',', '.')
  // Leeg is geen keuze maar een halve invoer. Wissen kan wel, maar dan typ je een
  // nul: een reeks maanden legen hoort een handeling te zijn en geen misklik.
  const ready = '' !== value && Number.isFinite(Number(value)) && Number(value) >= 0 && range.length > 0

  const change = (next: boolean) => {
    setOpen(next)
    // Elke keer schoon open: het paneel hoort te tonen wat je nu gaat doen, niet
    // wat je de vorige keer deed.
    if (next) {
      setFrom(start)
      setTo(start)
      setHours('')
    }
  }

  const submit = () => {
    if (!ready) return
    onFill(range, value)
    setOpen(false)
  }

  return (
    <PopoverSheet
      open={open}
      onOpenChange={change}
      title={`Reeks vullen voor ${who}`}
      className="w-80"
      trigger={
        <button
          type="button"
          aria-label={`Reeks vullen voor ${who}`}
          // Dezelfde rij-actieregel als elders: op touch altijd zichtbaar, op de
          // muis pas als je in de buurt bent. Openstaand blijft hij staan, anders
          // verdwijnt de knop onder je eigen paneel.
          className="btn btn-ghost h-6 w-6 shrink-0 p-0 text-ink-faint transition-opacity data-[state=open]:opacity-100 md:opacity-0 md:focus-visible:opacity-100 md:group-hover/rij:opacity-100"
        >
          <CalendarRange className="h-3.5 w-3.5" strokeWidth={1.85} />
        </button>
      }
    >
      <div className="space-y-3 p-4">
        {/* Label links, veld rechts: de maandnamen dragen hun jaartal, en naast
            elkaar geperst zou dat er juist afvallen. */}
        <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
          <span id={`${ids}-van`} className="label-caps">
            Van
          </span>
          <Select
            ariaLabelledby={`${ids}-van`}
            value={from}
            onValueChange={(next) => {
              setFrom(next)
              if (next > to) setTo(next)
            }}
            options={options}
            searchPlaceholder="Maand zoeken"
            className="h-8 w-full text-xs"
          />

          <span id={`${ids}-tot`} className="label-caps">
            T/m
          </span>
          <Select
            ariaLabelledby={`${ids}-tot`}
            value={to}
            onValueChange={(next) => {
              setTo(next)
              if (next < from) setFrom(next)
            }}
            options={options}
            searchPlaceholder="Maand zoeken"
            className="h-8 w-full text-xs"
          />

          <label htmlFor={`${ids}-uren`} className="label-caps">
            Uren
          </label>
          <input
            id={`${ids}-uren`}
            type="text"
            inputMode="decimal"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return
              event.preventDefault()
              submit()
            }}
            className="field num h-8 w-full text-sm"
          />
        </div>

        <p className="text-xs text-ink-faint">
          {range.length === 1 ? 'Zet 1 maand' : `Zet ${range.length} maanden`} op dit aantal uur; wat er stond wordt
          overschreven. Nul maakt ze leeg.
        </p>

        <button type="button" className="btn btn-primary h-8 w-full text-xs" disabled={!ready} onClick={submit}>
          Invullen
        </button>
      </div>
    </PopoverSheet>
  )
}
